import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Subject, TimeoutError } from 'rxjs';
import { takeUntil, timeout } from 'rxjs/operators';
import { HttpErrorResponse, HttpEvent, HttpEventType } from '@angular/common/http';
import { PublicFormService, PublicFormSubmitPayload, PublicFormViewPayload, PublicFormSubmitResponse, FormFieldValue, PublicFileUploadResponse } from '../../../marketing-forms/service/public-forms.service';
import { PublicFormRenderModel, PublicFormFieldRenderItem } from '../../../marketing-forms/models/public-form.model';
import { FormStepConfig } from '../../../marketing-forms/models/form.model';
import { COUNTRIES_DATA, CountryData } from '../../../../shared/data/countries.data';
import { evaluateRule, parseConditionalRule, FormValues } from '../../../marketing-forms/utils/condition-evaluator';
import { parseStepsConfig, parseStepRoutingConfig, resolveNextStep } from '../../../marketing-forms/utils/step-routing';
import { CountrySelectComponent } from '../../../../shared/components/form/country-select/country-select.component';

@Component({
  selector: 'app-public-form',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, CountrySelectComponent],
  templateUrl: './public-form.component.html',
})
export class PublicFormComponent implements OnInit, OnDestroy {
  private route = inject(ActivatedRoute);
  private publicFormsService = inject(PublicFormService);
  private fb = inject(FormBuilder);

  private readonly destroy$ = new Subject<void>();
  private redirectTimeout: ReturnType<typeof setTimeout> | null = null;

  formData: PublicFormRenderModel | null = null;
  dynamicForm!: FormGroup;

  loading = true;
  error = false;
  isSubmitting = false;
  submitSuccess = false;
  submitError: string | null = null;
  successMessage: string | null = null;
  readonly currentYear = new Date().getFullYear();

  private slug = '';

  private readonly VISITOR_TOKEN_KEY = 'mkt_visitor_token';

  // Checkboxes: Map<fieldKey, Set<optionValue>>
  private checkboxValues = new Map<string, Set<string>>();

  // Geo country/region: Map<fieldKey, {country, region}>
  private geoValues = new Map<string, { country: string; region: string }>();

  readonly countries: CountryData[] = COUNTRIES_DATA;
  readonly npsScores = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

  // Hidden fields populated from URL params or defaultFixedValue
  private hiddenValues = new Map<string, string>();

  // File upload: estado por fieldKey
  fileUploadValues = new Map<string, { url: string; name: string; size: number }>();
  private fileUploading = new Set<string>();
  fileUploadErrors = new Map<string, string>();
  /** Progreso 0-100 por fieldKey; -1 = subiendo pero sin total conocido. */
  fileUploadProgress = new Map<string, number>();

  private readonly FILE_MAX_BYTES = 10 * 1024 * 1024; // 10 MB

  /**
   * Si la subida no reporta NINGÚN avance en este tiempo se aborta con error. Es un
   * timeout de inactividad, no de duración total: un archivo grande por una conexión
   * lenta sigue avanzando y no se cancela.
   */
  private readonly UPLOAD_STALL_MS = 30_000;

  /** Aviso de «Enviar» pulsado con una subida en curso; se retira solo cuando esta termina. */
  private static readonly WAIT_FOR_UPLOAD_MSG = 'Espera a que termine de subirse el archivo adjunto.';

  get isAnyFileUploading(): boolean {
    return this.fileUploading.size > 0;
  }

  isFileUploading(fieldKey: string): boolean {
    return this.fileUploading.has(fieldKey);
  }

  /** Porcentaje a pintar mientras sube; null si aún no se conoce. */
  uploadProgressOf(fieldKey: string): number | null {
    const p = this.fileUploadProgress.get(fieldKey);
    return p === undefined || p < 0 ? null : p;
  }

  // Parsed styleConfig
  private parsedStyle: Record<string, unknown> = {};

  // Parsed gdprConfig — privacy link settings (form-level)
  private parsedGdprCfg: { privacyUrl?: string; linkText?: string; policyText?: string } = {};

  policyModalOpen = false;

  // Tracks all current field values for conditional rule evaluation
  formValues: FormValues = {};
  conditionalRequiredErrors = new Set<string>();

  activeHelpField: string | null = null;

  toggleHelp(fieldKey: string): void {
    this.activeHelpField = this.activeHelpField === fieldKey ? null : fieldKey;
  }

  // ── Multi-paso (inc22 / inc23) ──────────────────────────────────────────────
  currentStep = 1;
  private stepHistory: number[] = [];
  private stepsCfg: FormStepConfig[] = [];

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.redirectTimeout !== null) {
      clearTimeout(this.redirectTimeout);
    }
  }

  ngOnInit(): void {
    this.slug = this.route.snapshot.paramMap.get('slug') ?? '';
    if (this.slug) {
      this.loadForm();
    } else {
      this.error = true;
      this.loading = false;
    }
  }

  private loadForm(): void {
    this.publicFormsService.getFormBySlug(this.slug).pipe(takeUntil(this.destroy$)).subscribe({
      next: (data) => {
        // Normalize fieldType to lowercase for consistent @case comparisons
        this.formData = {
          ...data,
          fields: data.fields.map(f => ({ ...f, fieldType: f.fieldType.toLowerCase() })),
        };
        this.parseStyle();
        this.parseGdprConfig();
        this.stepsCfg = parseStepsConfig(this.formData.stepsConfig);
        this.buildForm();
        this.loading = false;
        this.recordView(data.id);
      },
      error: () => {
        this.error = true;
        this.loading = false;
      },
    });
  }

  private recordView(formId: number): void {
    const qp = this.route.snapshot.queryParamMap;
    const payload: PublicFormViewPayload = {
      visitorToken: this.getOrCreateVisitorToken(),
      pageUrl: window.location.href,
      utmSource: qp.get('utm_source') ?? undefined,
      utmMedium: qp.get('utm_medium') ?? undefined,
      utmCampaign: qp.get('utm_campaign') ?? undefined,
    };
    this.publicFormsService.recordView(formId, payload).pipe(takeUntil(this.destroy$)).subscribe();
  }

  private parseStyle(): void {
    if (!this.formData?.styleConfig) return;
    try {
      this.parsedStyle = JSON.parse(this.formData.styleConfig) as Record<string, unknown>;
    } catch {
      this.parsedStyle = {};
    }
  }

  private parseGdprConfig(): void {
    if (!this.formData?.gdprConfig) return;
    try {
      this.parsedGdprCfg = JSON.parse(this.formData.gdprConfig) as { privacyUrl?: string; linkText?: string; policyText?: string };
    } catch {
      this.parsedGdprCfg = {};
    }
  }

  get gdprPrivacyUrl(): string {
    return this.parsedGdprCfg.privacyUrl?.trim() ?? '';
  }

  get gdprLinkText(): string {
    return this.parsedGdprCfg.linkText?.trim() || 'Política de privacidad';
  }

  get gdprPolicyText(): string {
    return this.parsedGdprCfg.policyText?.trim() ?? '';
  }

  openPolicyModal(): void  { this.policyModalOpen = true; }
  closePolicyModal(): void { this.policyModalOpen = false; }

  /**
   * Parsea `validationRules` (JSONB del backend) y devuelve los ValidatorFn
   * correspondientes. Se ignoran reglas malformadas o con regex inválido para
   * no bloquear el renderizado del formulario.
   */
  private buildFieldValidators(field: { validationRules?: string }): ValidatorFn[] {
    if (!field.validationRules) return [];
    let rules: Record<string, unknown>;
    try {
      rules = JSON.parse(field.validationRules) as Record<string, unknown>;
    } catch {
      return [];
    }
    const out: ValidatorFn[] = [];
    if (typeof rules['minLength'] === 'number') out.push(Validators.minLength(rules['minLength']));
    if (typeof rules['maxLength'] === 'number') out.push(Validators.maxLength(rules['maxLength']));
    if (typeof rules['min']       === 'number') out.push(Validators.min(rules['min']));
    if (typeof rules['max']       === 'number') out.push(Validators.max(rules['max']));
    if (typeof rules['pattern']   === 'string' && rules['pattern'].trim()) {
      try {
        // Pasamos RegExp (no string) para evitar que Angular envuelva con ^...$
        // y rompa patrones que ya traen anchors propios (ej. ^[A-Z]{2,4}$).
        out.push(Validators.pattern(new RegExp(rules['pattern'])));
      } catch {
        // Regex inválido almacenado en BD — se omite silenciosamente.
      }
    }
    return out;
  }

  private buildForm(): void {
    const group: Record<string, unknown> = {};
    const qp = this.route.snapshot.queryParamMap;

    this.formData?.fields.forEach(field => {
      if (field.isHidden) {
        const fromUrl = field.urlParamName ? (qp.get(field.urlParamName) ?? '') : '';
        this.hiddenValues.set(field.fieldKey, fromUrl || field.defaultFixedValue || '');
        return;
      }

      if (field.fieldType === 'checkboxes') {
        this.checkboxValues.set(field.fieldKey, new Set());
        return;
      }

      if (field.fieldType === 'geo_country_region') {
        this.geoValues.set(field.fieldKey, { country: '', region: '' });
        return;
      }

      if (field.fieldType === 'file_upload') {
        return;
      }

      const validators: ValidatorFn[] = [];
      if (field.isRequired) {
        if (field.fieldType === 'gdpr_consent') {
          validators.push((ctrl) => ctrl.value === true ? null : { required: true });
        } else {
          validators.push(Validators.required);
        }
      }
      if (field.fieldType === 'email') validators.push(Validators.email);
      validators.push(...this.buildFieldValidators(field));

      // gdpr_consent uses boolean, rest use string
      const defaultVal = field.fieldType === 'gdpr_consent' ? false : '';
      group[field.fieldKey] = [defaultVal, validators];
    });

    this.dynamicForm = this.fb.group(group);
    this.formValues = this.dynamicForm.value as FormValues;
    this.dynamicForm.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(vals => {
      this.formValues = { ...this.formValues, ...(vals as FormValues) };
    });
  }

  // ── Geo country/region helpers ──────────────────────────────────────────────

  onCountryChange(fieldKey: string, countryCode: string): void {
    this.geoValues.set(fieldKey, { country: countryCode, region: '' });
    this.formValues = { ...this.formValues, [fieldKey]: countryCode };
  }

  onRegionChange(fieldKey: string, region: string): void {
    const current = this.geoValues.get(fieldKey) ?? { country: '', region: '' };
    this.geoValues.set(fieldKey, { ...current, region });
    this.formValues = { ...this.formValues, [fieldKey + '_region']: region };
  }

  geoCountry(fieldKey: string): string {
    return this.geoValues.get(fieldKey)?.country ?? '';
  }

  geoRegion(fieldKey: string): string {
    return this.geoValues.get(fieldKey)?.region ?? '';
  }

  regionsFor(countryCode: string): string[] {
    return this.countries.find(c => c.code === countryCode)?.regions ?? [];
  }

  // ── File upload helpers ─────────────────────────────────────────────────────

  onFileSelected(fieldKey: string, event: Event): void {
    if (!this.formData) return;
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;

    if (file.size > this.FILE_MAX_BYTES) {
      this.fileUploadErrors.set(fieldKey, 'El archivo supera el límite de 10 MB');
      input.value = '';
      return;
    }

    this.fileUploadErrors.delete(fieldKey);
    this.fileUploadValues.delete(fieldKey);
    this.fileUploading.add(fieldKey);
    this.fileUploadProgress.set(fieldKey, -1);

    this.publicFormsService.uploadFile(this.formData.id, file)
      .pipe(
        // Timeout de INACTIVIDAD: se reinicia con cada evento de progreso, así que solo
        // salta si la subida se queda realmente parada. Sin esto, una petición que nunca
        // resuelve dejaba el campo en «Subiendo archivo...» indefinidamente.
        timeout({ each: this.UPLOAD_STALL_MS }),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: (event: HttpEvent<PublicFileUploadResponse>) => {
          if (event.type === HttpEventType.UploadProgress) {
            this.fileUploadProgress.set(
              fieldKey,
              event.total ? Math.round((event.loaded / event.total) * 100) : -1,
            );
            return;
          }
          if (event.type !== HttpEventType.Response) return;

          const res = event.body;
          if (!res?.publicUrl) {
            // 2xx sin cuerpo utilizable: tratarlo como fallo en vez de dejar el campo
            // colgado esperando un archivo que nunca se adjuntó.
            this.failUpload(fieldKey, input, 'No se pudo subir el archivo. Inténtalo de nuevo.');
            return;
          }
          this.fileUploading.delete(fieldKey);
          this.fileUploadProgress.delete(fieldKey);
          this.fileUploadValues.set(fieldKey, { url: res.publicUrl, name: res.originalName, size: res.fileSize });
          this.clearWaitForUploadNotice();
        },
        error: (err: unknown) => {
          this.failUpload(fieldKey, input, this.uploadErrorMessage(err));
        },
      });
  }

  /** Deja el campo listo para reintentar: sin spinner, con error y con el input limpio. */
  private failUpload(fieldKey: string, input: HTMLInputElement, message: string): void {
    this.fileUploading.delete(fieldKey);
    this.fileUploadProgress.delete(fieldKey);
    this.fileUploadErrors.set(fieldKey, message);
    input.value = '';
    this.clearWaitForUploadNotice();
  }

  /** Retira el aviso «espera a que termine…» en cuanto no queda ninguna subida en curso. */
  private clearWaitForUploadNotice(): void {
    if (this.submitError === PublicFormComponent.WAIT_FOR_UPLOAD_MSG && !this.isAnyFileUploading) {
      this.submitError = null;
    }
  }

  /**
   * Mensaje concreto por tipo de fallo. Antes todo caía en el mismo texto genérico, así que
   * un archivo demasiado grande, un formato no admitido y un corte de red eran
   * indistinguibles para quien rellena el formulario.
   */
  private uploadErrorMessage(err: unknown): string {
    if (err instanceof TimeoutError) {
      return 'La subida se quedó detenida. Revisa tu conexión e inténtalo de nuevo.';
    }
    if (err instanceof HttpErrorResponse) {
      switch (err.status) {
        case 0:
          return 'Se perdió la conexión durante la subida. Inténtalo de nuevo.';
        case 400:
          return 'Ese tipo de archivo no está permitido. Usa una imagen (JPG, PNG) o un documento (PDF, DOCX, XLSX, TXT).';
        case 413:
          return 'El archivo es demasiado grande. El límite es 10 MB.';
        case 429:
          return 'Has subido demasiados archivos seguidos. Espera un minuto e inténtalo de nuevo.';
        case 502:
        case 503:
        case 504:
          return 'El servidor no pudo procesar la subida. Inténtalo de nuevo en un momento.';
      }
    }
    return 'No se pudo subir el archivo. Inténtalo de nuevo.';
  }

  removeFile(fieldKey: string): void {
    this.fileUploadValues.delete(fieldKey);
    this.fileUploadErrors.delete(fieldKey);
    this.fileUploadProgress.delete(fieldKey);
  }

  formatFileSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  // ── Checkbox helpers ────────────────────────────────────────────────────────

  onCheckboxChange(fieldKey: string, optionValue: string, checked: boolean): void {
    const set = this.checkboxValues.get(fieldKey) ?? new Set<string>();
    checked ? set.add(optionValue) : set.delete(optionValue);
    this.checkboxValues.set(fieldKey, set);
    this.formValues = { ...this.formValues, [fieldKey]: [...set].join(',') };
  }

  isChecked(fieldKey: string, optionValue: string): boolean {
    return this.checkboxValues.get(fieldKey)?.has(optionValue) ?? false;
  }

  // ── Visible fields ──────────────────────────────────────────────────────────

  // ── Multi-paso helpers (inc22 / inc23) ──────────────────────────────────────

  get isMultiStep(): boolean {
    return !!(this.formData?.isMultiStep && (this.formData?.totalSteps ?? 0) > 1);
  }

  get totalSteps(): number {
    return this.formData?.totalSteps ?? 1;
  }

  get isLastStep(): boolean {
    return !this.isMultiStep || this.currentStep >= this.totalSteps;
  }

  private currentStepConfig(): FormStepConfig | undefined {
    if (!this.isMultiStep) return undefined;
    return this.stepsCfg.find(s => s.step === this.currentStep);
  }

  get stepTitle(): string {
    return this.currentStepConfig()?.title?.trim() || '';
  }

  get stepDescription(): string {
    return this.currentStepConfig()?.description?.trim() || '';
  }

  get stepImageUrl(): string {
    return this.currentStepConfig()?.imageUrl?.trim() || '';
  }

  goPrevStep(): void {
    if (this.stepHistory.length) {
      this.currentStep = this.stepHistory.pop()!;
    } else if (this.currentStep > 1) {
      this.currentStep--;
    }
  }

  goNextStep(): void {
    const routing = parseStepRoutingConfig(this.formData?.stepRoutingConfig);
    const next = resolveNextStep(routing, this.currentStep, this.formValues, this.totalSteps);
    if (next !== this.currentStep) {
      this.stepHistory.push(this.currentStep);
      this.currentStep = next;
    }
  }

  private applyConditionalFilter(fields: PublicFormFieldRenderItem[]): PublicFormFieldRenderItem[] {
    return fields.filter(f => {
      const rule = parseConditionalRule(f.conditionalRules);
      if (!rule) return true;
      const applies = evaluateRule(rule, this.formValues);
      if (rule.action === 'show') return applies;
      if (rule.action === 'hide') return !applies;
      return true; // 'required' action: always visible
    });
  }

  get visibleFields(): PublicFormFieldRenderItem[] {
    const all = this.applyConditionalFilter(this.formData?.fields.filter(f => !f.isHidden) ?? []);
    if (!this.isMultiStep) return all;
    return all.filter(f => (f.stepNumber ?? 1) === this.currentStep);
  }

  /** Todos los campos visibles (todos los pasos) — para validar el envío final. */
  private get allVisibleFields(): PublicFormFieldRenderItem[] {
    return this.applyConditionalFilter(this.formData?.fields.filter(f => !f.isHidden) ?? []);
  }

  isFieldRequired(field: PublicFormFieldRenderItem): boolean {
    if (field.isRequired) return true;
    const rule = parseConditionalRule(field.conditionalRules);
    if (!rule || rule.action !== 'required') return false;
    return evaluateRule(rule, this.formValues);
  }

  // ── Style helpers ───────────────────────────────────────────────────────────

  get containerStyle(): Record<string, string | undefined> {
    const s = this.parsedStyle;
    return {
      backgroundColor: (s['backgroundColor'] as string) || undefined,
      color: (s['textColor'] as string) || undefined,
      fontFamily: (s['fontFamily'] as string) || undefined,
    };
  }

  get buttonStyle(): Record<string, string | undefined> {
    const s = this.parsedStyle;
    const radius = s['borderRadius'] != null ? `${s['borderRadius']}px` : undefined;
    return {
      backgroundColor: (s['primaryColor'] as string) || undefined,
      color: (s['buttonTextColor'] as string) || undefined,
      borderRadius: radius,
    };
  }

  get inputBorderRadius(): string | undefined {
    const r = this.parsedStyle['borderRadius'];
    return r != null ? `${r}px` : undefined;
  }

  // ── Submit ──────────────────────────────────────────────────────────────────

  onSubmit(): void {
    if (!this.formData) return;
    this.submitError = null;
    this.dynamicForm.markAllAsTouched();
    this.conditionalRequiredErrors.clear();

    // Antes esto era un `return` MUDO: si una subida se quedaba en curso, pulsar «Enviar»
    // no hacía absolutamente nada y parecía que el botón estuviera roto.
    if (this.isAnyFileUploading) {
      this.submitError = PublicFormComponent.WAIT_FOR_UPLOAD_MSG;
      return;
    }

    const visible = this.allVisibleFields;
    let hasErrors = visible.some(f => {
      const ctrl = this.dynamicForm.get(f.fieldKey);
      return ctrl ? ctrl.invalid : false;
    });

    // Validar file_upload requeridos
    for (const field of visible) {
      if (field.fieldType === 'file_upload' && field.isRequired && !this.fileUploadValues.has(field.fieldKey)) {
        this.fileUploadErrors.set(field.fieldKey, 'Este campo es obligatorio');
        hasErrors = true;
      }
    }

    for (const field of visible) {
      const rule = parseConditionalRule(field.conditionalRules);
      if (rule?.action === 'required' && evaluateRule(rule, this.formValues)) {
        const val = this.formValues[field.fieldKey];
        const isEmpty = !val || (typeof val === 'string' && val.trim() === '');
        if (isEmpty) {
          this.conditionalRequiredErrors.add(field.fieldKey);
          hasErrors = true;
        }
      }
    }

    if (hasErrors) return;

    this.isSubmitting = true;
    const fields: FormFieldValue[] = [];

    const visibleKeys = new Set(this.allVisibleFields.map(f => f.fieldKey));

    this.formData.fields.forEach(field => {
      // Skip fields hidden by conditional rules (but keep system-hidden fields)
      if (!field.isHidden && !visibleKeys.has(field.fieldKey)) return;

      // Hidden fields
      if (field.isHidden) {
        fields.push({
          fieldKey: field.fieldKey,
          rawValue: this.hiddenValues.get(field.fieldKey) ?? null,
          selectedOptionId: null,
        });
        return;
      }

      // Geo country/region: serialized as "countryCode|regionName"
      if (field.fieldType === 'geo_country_region') {
        const geo = this.geoValues.get(field.fieldKey) ?? { country: '', region: '' };
        const countryName = this.countries.find(c => c.code === geo.country)?.name ?? geo.country;
        const rawValue = countryName && geo.region
          ? `${countryName}|${geo.region}`
          : countryName || null;
        fields.push({ fieldKey: field.fieldKey, rawValue, selectedOptionId: null });
        return;
      }

      // File upload: rawValue = publicUrl devuelta por el backend
      if (field.fieldType === 'file_upload') {
        fields.push({
          fieldKey: field.fieldKey,
          rawValue: this.fileUploadValues.get(field.fieldKey)?.url ?? null,
          selectedOptionId: null,
        });
        return;
      }

      // Checkboxes: comma-separated selected option values
      if (field.fieldType === 'checkboxes') {
        const selected = [...(this.checkboxValues.get(field.fieldKey) ?? [])];
        fields.push({
          fieldKey: field.fieldKey,
          rawValue: selected.length ? selected.join(',') : null,
          selectedOptionId: null,
        });
        return;
      }

      const rawValue = this.dynamicForm.get(field.fieldKey)?.value;

      // GDPR consent: boolean → 'true'/'false' string
      if (field.fieldType === 'gdpr_consent') {
        fields.push({
          fieldKey: field.fieldKey,
          rawValue: rawValue ? 'true' : 'false',
          selectedOptionId: null,
        });
        return;
      }

      // Dropdown / radio: resolve selectedOptionId from options list
      if (field.fieldType === 'dropdown' || field.fieldType === 'radio') {
        const option = field.options?.find(o => o.optionValue === rawValue) ?? null;
        fields.push({
          fieldKey: field.fieldKey,
          rawValue: rawValue || null,
          selectedOptionId: option?.id ?? null,
        });
        return;
      }

      // All other text-based fields
      fields.push({
        fieldKey: field.fieldKey,
        rawValue: rawValue != null && rawValue !== '' ? String(rawValue) : null,
        selectedOptionId: null,
      });
    });

    const qp = this.route.snapshot.queryParamMap;
    const payload: PublicFormSubmitPayload = {
      visitorToken: this.getOrCreateVisitorToken(),
      pageUrl: window.location.href,
      utmSource: qp.get('utm_source') ?? undefined,
      utmMedium: qp.get('utm_medium') ?? undefined,
      utmCampaign: qp.get('utm_campaign') ?? undefined,
      fields,
    };

    this.publicFormsService.submitForm(this.formData.id, payload).pipe(takeUntil(this.destroy$)).subscribe({
      next: (response: PublicFormSubmitResponse) => {
        this.isSubmitting = false;
        this.handlePostSubmit(response);
      },
      error: () => {
        this.isSubmitting = false;
        this.submitError = 'No se pudo enviar el formulario. Por favor, inténtalo de nuevo.';
      },
    });
  }

  private handlePostSubmit(response: PublicFormSubmitResponse): void {
    this.successMessage = response.postSubmitMessage ?? null;
    this.submitSuccess = true;

    if ((response.postSubmitAction === 'REDIRECT' || response.postSubmitAction === 'BOTH')
        && response.postSubmitRedirectUrl) {
      const delay = (response.postSubmitRedirectDelayS ?? 0) * 1000;
      this.redirectTimeout = setTimeout(() => {
        window.location.href = response.postSubmitRedirectUrl!;
      }, Math.max(delay, 0));
    }
  }

  // ── Visitor token ───────────────────────────────────────────────────────────

  private getOrCreateVisitorToken(): string {
    let token = localStorage.getItem(this.VISITOR_TOKEN_KEY);
    if (!token) {
      token = crypto.randomUUID();
      localStorage.setItem(this.VISITOR_TOKEN_KEY, token);
    }
    return token;
  }

  // ── Template helpers ────────────────────────────────────────────────────────

  hasError(fieldKey: string): boolean {
    const ctrl = this.dynamicForm?.get(fieldKey);
    return !!(ctrl?.invalid && ctrl?.touched) || this.conditionalRequiredErrors.has(fieldKey);
  }

  /** Devuelve el primer mensaje de error relevante para el campo, o null si no hay error visible. */
  getFieldError(fieldKey: string): string | null {
    if (this.conditionalRequiredErrors.has(fieldKey)) return 'Este campo es obligatorio.';
    const e = this.dynamicForm?.get(fieldKey)?.errors as Record<string, unknown> | null;
    if (!e) return null;
    if (e['required'])  return 'Este campo es obligatorio.';
    if (e['email'])     return 'Introduce un correo electrónico válido.';
    if (e['minlength']) return `Debe tener al menos ${(e['minlength'] as Record<string, number>)['requiredLength']} caracteres.`;
    if (e['maxlength']) return `Debe tener como máximo ${(e['maxlength'] as Record<string, number>)['requiredLength']} caracteres.`;
    if (e['pattern'])   return 'El formato introducido no es válido.';
    if (e['min'])       return `El valor mínimo es ${(e['min'] as Record<string, number>)['min']}.`;
    if (e['max'])       return `El valor máximo es ${(e['max'] as Record<string, number>)['max']}.`;
    return 'Campo inválido.';
  }

  get submitLabel(): string {
    return this.formData?.submitButtonLabel?.trim() || 'Enviar';
  }

  columnClass(span: number): string {
    const map: Record<number, string> = {
      1: 'col-span-1',
      2: 'col-span-1 sm:col-span-2',
      3: 'col-span-1 sm:col-span-3',
    };
    return map[span] ?? 'col-span-1 sm:col-span-3';
  }
}
